
import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface MapProps {
  services: Array<{
    id: string;
    service_name?: string;
    title?: string;
    address?: string;
    profiles?: {
      full_name?: string;
      practice_address?: string;
    };
  }>;
}

export const Map = ({ services }: MapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const initializeMap = async () => {
      try {
        // Fetch Mapbox token from Edge Function with retry logic
        const getMapboxToken = async (retries = 3): Promise<string> => {
          try {
            const { data, error } = await supabase.functions.invoke('get-mapbox-token')
            
            if (error) {
              console.error('Error fetching Mapbox token:', error)
              throw error
            }

            const { token } = data as { token: string }
            if (!token) {
              throw new Error('Invalid token received')
            }

            return token
          } catch (error) {
            if (retries > 0) {
              await new Promise(resolve => setTimeout(resolve, 1000))
              return getMapboxToken(retries - 1)
            }
            throw error
          }
        }

        const mapboxToken = await getMapboxToken()

        // Initialize map if not already initialized
        if (!map.current && mapContainer.current) {
          mapboxgl.accessToken = mapboxToken;

          map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: "mapbox://styles/mapbox/streets-v12",
            center: [2.3522, 48.8566], // Default center (Paris)
            zoom: 11,
            pitchWithRotate: false,
            dragRotate: false
          });

          // Add navigation control with a more compact UI
          map.current.addControl(
            new mapboxgl.NavigationControl({
              showCompass: false,
              visualizePitch: false
            }),
            "top-right"
          );

          // Add scale control
          map.current.addControl(
            new mapboxgl.ScaleControl({
              maxWidth: 100,
              unit: 'metric'
            }),
            'bottom-left'
          );
        }

        const geocodeAddress = async (address: string, retries = 2): Promise<[number, number] | null> => {
          try {
            const encodedAddress = encodeURIComponent(address);
            console.log('Geocoding address:', address);
            
            const response = await fetch(
              `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${mapboxToken}`,
              {
                headers: {
                  'Accept': 'application/json'
                }
              }
            );
            
            if (!response.ok) {
              throw new Error(`Geocoding failed with status ${response.status}`);
            }

            const data = await response.json();
            console.log('Geocoding response:', data);
            
            if (data.features && data.features.length > 0) {
              const [lng, lat] = data.features[0].center;
              return [lng, lat];
            }
            return null;
          } catch (error) {
            console.error("Geocoding error:", error);
            if (retries > 0) {
              console.log(`Retrying geocoding for address: ${address}. Attempts remaining: ${retries}`);
              await new Promise(resolve => setTimeout(resolve, 1000));
              return geocodeAddress(address, retries - 1);
            }
            return null;
          }
        };

        // Clear existing markers
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];

        // Create bounds object
        const bounds = new mapboxgl.LngLatBounds();
        let validLocationsCount = 0;

        // Process services sequentially to avoid rate limiting
        for (const item of services) {
          const address = item.profiles?.practice_address || item.address;
          if (!address) continue;

          // Add delay between geocoding requests to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 200));

          const coordinates = await geocodeAddress(address);
          if (!coordinates) {
            console.log(`Failed to geocode address: ${address}`);
            continue;
          }

          validLocationsCount++;
          bounds.extend(coordinates as [number, number]);

          // Create custom marker element
          const el = document.createElement("div");
          el.className = "marker";
          el.style.width = "32px";
          el.style.height = "32px";
          el.style.backgroundImage = "url('/placeholder.svg')";
          el.style.backgroundSize = "cover";
          el.style.cursor = "pointer";
          el.style.borderRadius = "50%";
          el.style.border = "2px solid white";
          el.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
          el.style.transition = "transform 0.2s ease";

          // Add hover effect
          el.addEventListener('mouseenter', () => {
            el.style.transform = 'scale(1.1)';
          });
          el.addEventListener('mouseleave', () => {
            el.style.transform = 'scale(1)';
          });

          // Create popup with service/product information
          const popup = new mapboxgl.Popup({ 
            offset: 25,
            closeButton: false,
            closeOnClick: true,
            maxWidth: '300px',
            className: 'custom-popup'
          }).setHTML(`
            <div class="p-3 space-y-2">
              <h3 class="font-semibold text-base">${item.service_name || item.title}</h3>
              <p class="text-sm text-muted-foreground">${item.profiles?.full_name || "Professionnel"}</p>
              <p class="text-sm">${address}</p>
              <a 
                href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}"
                target="_blank"
                rel="noopener noreferrer"
                class="text-xs text-primary hover:underline"
              >
                Ouvrir dans Google Maps
              </a>
            </div>
          `);

          // Create and store marker
          const marker = new mapboxgl.Marker(el)
            .setLngLat(coordinates as [number, number])
            .setPopup(popup)
            .addTo(map.current);

          markersRef.current.push(marker);
        }

        // Adjust map bounds if we have valid locations
        if (validLocationsCount > 0) {
          map.current.fitBounds(bounds, {
            padding: { top: 50, bottom: 50, left: 50, right: 50 },
            maxZoom: 15,
            duration: 1000
          });
        } else {
          toast({
            variant: "destructive",
            title: "Erreur",
            description: "Aucun service ou produit avec une adresse valide n'a été trouvé."
          });
        }

      } catch (error) {
        console.error("Map initialization error:", error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger la carte. Veuillez réessayer."
        });
      } finally {
        setLoading(false);
      }
    };

    initializeMap();

    // Cleanup function
    return () => {
      if (map.current) {
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];
        map.current.remove();
        map.current = null;
      }
    };
  }, [services, toast]);

  return (
    <div className="relative w-full h-[500px] rounded-lg overflow-hidden">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-50">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}
      <div ref={mapContainer} className="w-full h-full" />
      <style>
        {`
          .mapboxgl-popup-content {
            padding: 0 !important;
            border-radius: 8px !important;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
          }
          .mapboxgl-popup-tip {
            display: none !important;
          }
        `}
      </style>
    </div>
  );
};
